import React, { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Icon, { ICON_OPTIONS } from "@/components/Icon";
import RichTextEditor from "@/components/RichTextEditor";
import { t } from "@/i18n";
import { createLink, deleteLink, fetchLinks, reorderLinks, updateLink } from "@/api";
import { toast } from "sonner";

const EMPTY_FORM = {
  label_ru: "",
  label_en: "",
  url: "",
  content_ru: "",
  content_en: "",
  icon: "send",
};

const LinksManager = ({ lang, onChanged }) => {
  const [links, setLinks] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    try {
      setLinks(await fetchLinks());
    } catch (e) {
      console.error(e);
      toast.error("Failed to load links");
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const startEdit = (link) => {
    setEditingId(link.id);
    setForm({
      label_ru: link.label_ru,
      label_en: link.label_en,
      url: link.url || "",
      content_ru: link.content_ru || "",
      content_en: link.content_en || "",
      icon: link.icon || "send",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.label_ru || !form.label_en || (!form.url && !form.content_ru && !form.content_en)) {
      toast.error(t(lang, "fillLinkFields"));
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        await updateLink(editingId, form);
      } else {
        await createLink(form);
      }
      toast.success(t(lang, "saved"));
      cancelEdit();
      await refresh();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t(lang, "confirmDelete"))) return;
    try {
      await deleteLink(id);
      toast.success(t(lang, "deleted"));
      await refresh();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const move = async (index, dir) => {
    const newIndex = index + dir;
    if (newIndex < 0 || newIndex >= links.length) return;
    const next = [...links];
    const [item] = next.splice(index, 1);
    next.splice(newIndex, 0, item);
    setLinks(next);
    try {
      await reorderLinks(next.map((l) => l.id));
      onChanged?.();
    } catch (err) {
      toast.error("Reorder failed");
      refresh();
    }
  };

  return (
    <div className="space-y-5">
      <form onSubmit={handleSubmit} className="space-y-3 p-4 border border-white/10 rounded-md bg-black/40" data-testid="admin-link-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="font-mono-display text-[10px] uppercase tracking-widest text-zinc-400">{t(lang, "labelRu")}</Label>
            <Input
              value={form.label_ru}
              onChange={(e) => setForm({ ...form, label_ru: e.target.value })}
              data-testid="form-label-ru"
              className="bg-black border-white/15 mt-1"
            />
          </div>
          <div>
            <Label className="font-mono-display text-[10px] uppercase tracking-widest text-zinc-400">{t(lang, "labelEn")}</Label>
            <Input
              value={form.label_en}
              onChange={(e) => setForm({ ...form, label_en: e.target.value })}
              data-testid="form-label-en"
              className="bg-black border-white/15 mt-1"
            />
          </div>
        </div>
        <div>
          <Label className="font-mono-display text-[10px] uppercase tracking-widest text-zinc-400">{t(lang, "urlOptional")}</Label>
          <Input
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            data-testid="form-url"
            placeholder="https://t.me/..."
            className="bg-black border-white/15 mt-1"
          />
        </div>
        <div className="grid lg:grid-cols-2 gap-3">
          <div>
            <Label className="font-mono-display text-[10px] uppercase tracking-widest text-zinc-400">{t(lang, "contentRu")}</Label>
            <RichTextEditor
              value={form.content_ru}
              onChange={(html) => setForm((current) => ({ ...current, content_ru: html }))}
              placeholder={t(lang, "articleHint")}
              testId="form-content-ru"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="font-mono-display text-[10px] uppercase tracking-widest text-zinc-400">{t(lang, "contentEn")}</Label>
            <RichTextEditor
              value={form.content_en}
              onChange={(html) => setForm((current) => ({ ...current, content_en: html }))}
              placeholder={t(lang, "articleHint")}
              testId="form-content-en"
              className="mt-1"
            />
          </div>
        </div>
        <div>
          <Label className="font-mono-display text-[10px] uppercase tracking-widest text-zinc-400">Icon</Label>
          <Select value={form.icon} onValueChange={(v) => setForm({ ...form, icon: v })}>
            <SelectTrigger data-testid="form-icon" className="bg-black border-white/15 mt-1 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0a0a0a] border-white/15 text-white">
              {ICON_OPTIONS.map((name) => (
                <SelectItem key={name} value={name} className="capitalize">
                  <span className="flex items-center gap-2">
                    <Icon name={name} className="w-4 h-4" />
                    {name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button
            type="submit"
            disabled={submitting}
            data-testid="form-submit"
            className="bg-[#FF1493] hover:bg-[#ff3aa3] text-black font-bold uppercase tracking-widest"
          >
            {editingId ? <Pencil className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            {editingId ? t(lang, "save") : t(lang, "addLink")}
          </Button>
          {editingId && (
            <Button
              type="button"
              variant="outline"
              onClick={cancelEdit}
              data-testid="form-cancel"
              className="border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-white"
            >
              {t(lang, "cancel")}
            </Button>
          )}
        </div>
      </form>

      <div className="space-y-2" data-testid="admin-link-list">
        {links.map((link, idx) => (
          <div
            key={link.id}
            data-testid={`admin-link-row-${idx}`}
            className="flex items-center gap-3 p-3 border border-white/10 rounded-md bg-black/30"
          >
            <span className="grid place-items-center w-9 h-9 rounded-md border border-white/10 bg-black text-white">
              <Icon name={link.icon} className="w-4 h-4" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-mono-display text-sm text-white truncate uppercase tracking-wide">
                {link.label_ru} / {link.label_en}
              </div>
              <div className="font-mono-display text-[10px] text-zinc-500 truncate">
                {link.url || t(lang, "articleOnly")}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => move(idx, -1)} data-testid={`admin-up-${idx}`} className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-white" aria-label="up">
                <ArrowUp className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => move(idx, 1)} data-testid={`admin-down-${idx}`} className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-white" aria-label="down">
                <ArrowDown className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => startEdit(link)} data-testid={`admin-edit-${idx}`} className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-[#FF1493]" aria-label="edit">
                <Pencil className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => handleDelete(link.id)} data-testid={`admin-delete-${idx}`} className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-red-500" aria-label="delete">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LinksManager;
